import React, { useState, useContext } from "react";
import {
  getToken,
  setUserSession,
  getFormatDate,
  translate,
} from "../../../utils/Common";
import AuthContext from "../../../contexts/auth";
import { useForm } from "react-hook-form";
import Input from "../../../components/common/Input";
import Button from "../../../components/common/Button";
import { ConfirmModal } from "../../../components/common/ConfirmModal";

// icons
import * as FaIcons from "react-icons/fa";
import * as MdIcons from "react-icons/md";
import { FiEdit2, FiX } from "react-icons/fi";
import styles from "./FormPersonalInformation.module.css";

const FormPersonalInformation = ({
  updateSidebarInfo,
  userInformation,
  validateUpdatePwd,
  setValidateUpdatePwd,
  validateUpdatePwdLocker,
  setValidateUpdatePwdLocker
}) => {
  
  const { handleSubmit, register, formState: { errors }, clearErrors } = useForm();
  const UserContext = useContext(AuthContext);
  const [validate, setValidate] = useState(false);
  const [alertModal, setAlertModal] = useState({ open: false, title: '', message: '' });
  const [editConfirmOpen, setEditConfirmOpen] = useState(false);
  const [passwordConfirmOpen, setPasswordConfirmOpen] = useState(false);

  // send data to server for update
  const sendUpdate = async (data) => {
    try {
      data.idCliente = userInformation.IdCliente;
      const result = await UserContext.updateInfo(data);

      if (result) {
        setValidate(false);

        switch (result) {
          case "INFO_UPDATED_SUCCESS":
            UserContext.user.Nombre = data.name.toString().toUpperCase();
            UserContext.user.NombreSimple =
              data.name.toString().toUpperCase().substring(0, 1) +
              data.name.toString().toLowerCase().substring(1, data.name.length);
            UserContext.user.Apellidos = data.lastname.toString().toUpperCase();
            UserContext.user.ApellidoSimple =
              data.lastname.toString().toUpperCase().substring(0, 1) +
              data.lastname
                .toString()
                .toLowerCase()
                .substring(1, data.lastname.length);
            setUserSession(getToken(), UserContext.user);
            updateSidebarInfo();

            setAlertModal({
              open: true,
              title: translate("dashboard.userprofile.personal.successful_update"),
              message: translate("dashboard.userprofile.personal.success_message"),
            });
            break;

          case "INVALID_PARAMS":
          case "USER_NOT_AUTHORIZED":
          default:
            setAlertModal({
              open: true,
              title: translate("dashboard.userprofile.personal.error_header"),
              message: translate("dashboard.userprofile.personal.error_message"),
            });
            break;
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleCancelUpdate = () => {
    clearErrors();
    setValidate(false);
  };

  const handleEditClick = () => setEditConfirmOpen(true);
  const handlePasswordChangeClick = () => setPasswordConfirmOpen(true);

  return (
    <form onSubmit={handleSubmit(sendUpdate)} className={styles.form}>
      {/* Registration Date Badge */}
      {userInformation && (
        <div className={styles.registrationBadge}>
          <span className={styles.badgeText}>
            {translate("dashboard.userprofile.personal.registered_since")} {userInformation.FechaCreacion ? getFormatDate(userInformation.FechaCreacion.substring(10, 0)) : ""}
          </span>
        </div>
      )}

      {/* Form Grid */}
      <div className={styles.formGrid}>
        {/* Name */}
        <Input
          label={translate("dashboard.userprofile.personal.name_label")}
          defaultValue={UserContext.user?.NombreSimple || ""}
          disabled={!validate}
          name="name"
          type="text"
          error={errors.name?.message}
          required
          {...register("name", {
            required: {
              value: true,
              message: translate("dashboard.userprofile.personal.invalid_name"),
            },
            maxLength: {
              value: 32,
              message: translate("dashboard.userprofile.personal.name_max_length"),
            },
            pattern: {
              value: /^[a-zA-Z\s]+$/,
              message: translate("dashboard.userprofile.personal.no_numbers_allowed"),
            },
          })}
        />

        {/* Lastname */}
        <Input
          label={translate("dashboard.userprofile.personal.lastname_label")}
          defaultValue={UserContext.user?.ApellidoSimple || ""}
          disabled={!validate}
          name="lastname"
          type="text"
          error={errors.lastname?.message}
          required
          {...register("lastname", {
            required: {
              value: true,
              message: translate("dashboard.userprofile.personal.invalid_name"),
            },
            maxLength: {
              value: 32,
              message: translate("dashboard.userprofile.personal.name_max_length"),
            },
            pattern: {
              value: /^[a-zA-Z\s]+$/,
              message: translate("dashboard.userprofile.personal.no_numbers_allowed"),
            },
          })}
        />

        {/* Birth Date */}
        <Input
          label={translate("dashboard.userprofile.personal.birthdate_label")}
          value={
            userInformation
              ? userInformation.FechaNacimiento
                ? getFormatDate(userInformation?.FechaNacimiento.substring(10, 0))
                : ""
              : ""
          }
          disabled
          type="text"
        />

        {/* Phone */}
        <Input
          label={translate("dashboard.userprofile.personal.phone_label")}
          defaultValue={userInformation ? userInformation?.Telefono : ""}
          disabled={!validate}
          name="phoneNumber"
          type="text"
          error={errors.phoneNumber?.message}
          leftIcon={<FaIcons.FaPhoneAlt size={16} />}
          required
          {...register("phoneNumber", {
            required: {
              value: true,
              message: translate("dashboard.userprofile.personal.invalid_phone"),
            },
            pattern: {
              value: /^[0-9]+$/,
              message: translate("dashboard.userprofile.personal.invalid_phone"),
            },
            maxLength: {
              value: 32,
              message: translate("dashboard.userprofile.personal.phone_max_length"),
            },
          })}
        />

        {/* Email */}
        <Input
          label={translate("dashboard.userprofile.personal.email_label")}
          defaultValue={UserContext.user?.Email || ""}
          disabled={!validate}
          name="email"
          type="email"
          error={errors.email?.message}
          leftIcon={<MdIcons.MdEmail size={18} />}
          required
          {...register("email", {
            required: {
              value: true,
              message: translate("dashboard.userprofile.personal.invalid_email"),
            },
            maxLength: {
              value: 60,
              message: translate("dashboard.userprofile.personal.email_max_length"),
            },
          })}
        />
      </div>

      {/* Actions Section */}
      {!validateUpdatePwd && !validateUpdatePwdLocker && (
        <>
          {/* Password Change Info */}
          <div className={styles.passwordSection}>
            <p className={styles.infoText}>
              {translate("dashboard.userprofile.personal.change_password_info")}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePasswordChangeClick}
              leftIcon={<FiEdit2 size={16} />}
            >
              {translate("dashboard.userprofile.personal.update_password")}
            </Button>
          </div>

          {/* Form Actions */}
          <div className={styles.actions}>
            {validate && (
              <Button
                variant="outline"
                onClick={handleCancelUpdate}
                leftIcon={<FiX size={16} />}
              >
                {translate("dashboard.userprofile.updatepsw.cancel")}
              </Button>
            )}
            {userInformation && (
              validate ? (
                <Button variant="primary" type="submit" leftIcon={<FiEdit2 size={16} />}>
                  {translate("dashboard.userprofile.personal.update")}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleEditClick}
                  leftIcon={<FiEdit2 size={16} />}
                >
                  {translate("dashboard.userprofile.personal.update")}
                </Button>
              )
            )}
          </div>
        </>
      )}

      {/* Alert modal (solo OK) */}
      <ConfirmModal
        isOpen={alertModal.open}
        onClose={() => setAlertModal((p) => ({ ...p, open: false }))}
        title={alertModal.title}
        message={alertModal.message}
        confirmText={translate("dashboard.userprofile.personal.ok_button")}
        variant="primary"
        showCancelButton={false}
      />

      {/* Confirmar editar datos */}
      <ConfirmModal
        isOpen={editConfirmOpen}
        onClose={() => setEditConfirmOpen(false)}
        onConfirm={() => {
          setEditConfirmOpen(false);
          setValidate(true);
        }}
        title={translate("dashboard.userprofile.personal.attention")}
        message={translate("dashboard.userprofile.personal.confirmation_message")}
        confirmText={translate("dashboard.userprofile.personal.accept")}
        cancelText={translate("dashboard.userprofile.personal.cancel")}
        variant="primary"
      />

      {/* Confirmar cambio de contraseña */}
      <ConfirmModal
        isOpen={passwordConfirmOpen}
        onClose={() => setPasswordConfirmOpen(false)}
        onConfirm={() => {
          setPasswordConfirmOpen(false);
          setValidateUpdatePwd(true);
        }}
        title={translate("dashboard.userprofile.personal.attention")}
        message={translate("dashboard.userprofile.personal.confirm_password_change")}
        confirmText={translate("dashboard.userprofile.personal.accept")}
        cancelText={translate("dashboard.userprofile.personal.cancel")}
        variant="primary"
      />
    </form>
  );
};

export default FormPersonalInformation;
